import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { UserAddIcon, SearchIcon, XIcon } from "@heroicons/react/solid";
import tw from "tailwind-styled-components";
import styled from "styled-components";

import { setHumansFilterQuery } from "../../actions/humans";

import HumanListRow from "../../components/HumanListRow";
import Button from "../../components/Button";
import { createLoadingState } from "../../utils/selectors";
import { getFilteredHumans } from "../../selectors/humans";
import { useInfiniteListScroll } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import Input from "../../components/Input";

const HumanListColumnWrapper = tw.div`
flex-1 h-full px-1 
`;

const HumanListHeight = styled.ul`
  height: calc(100% - 70px);
`;

const HumanListWrapper = tw(HumanListHeight)`
divide-y divide-gray-200 pb-32 overflow-y-auto lg:pb-10
`;

const InputWrapper = tw.div`
relative rounded-md w-full
`;

const AddHumanWrapper = tw.div`
flex justify-end lg:justify-start mt-1 mb-2
`;

const ClearIcon = tw.div`
absolute 
inset-y-0 
right-0 
pr-3 
flex 
items-center 
cursor-pointer
`;

const HumanLogo = styled.img`
height: 22px;
position: relative;
right: 6px;
padding-left: 4px;
padding-right: 2px;
}
`;

const getLoadingState = createLoadingState(["humans"]);

const HumanListColumn = ({ selectedId }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const humans = useSelector((state) => getFilteredHumans(state));
  const comments = useSelector((state) => state.humanComments);
  const query = useSelector((state) => state.humansFilterQuery);
  const { loadingHumans } = useSelector((state) => getLoadingState(state));
  const [inputQuery, setInputQuery] = useState(query.searchTerm);

  useEffect(() => {
    // Handle focusing on humans on arrow down/up keypress
    const handler = (e) => {
      let activeElement = document.activeElement;

      // focus on search input
      if (e.key === "/") {
        const modalIsOpen =
          document.querySelector("#headlessui-portal-root") !== null;
        const focusIsNotOnInput =
          !activeElement ||
          (activeElement.tagName !== "INPUT" &&
            activeElement.tagName !== "TEXTAREA");

        if (focusIsNotOnInput && !modalIsOpen) {
          e.preventDefault();
          document.getElementById("human-search-input").focus();
        }
        return;
      }

      if (!activeElement) return;

      const isSearchInput = activeElement.id === "human-search-input";
      const isHumanRow = activeElement.classList.contains("human-list-row");

      if (!isSearchInput && !isHumanRow) return;

      if (isSearchInput && e.key === "ArrowDown") {
        e.preventDefault();
        const firstHumanRow = document.querySelector("ul#human-list > li");
        firstHumanRow && firstHumanRow.focus();
      } else if (isHumanRow && e.key === "ArrowDown") {
        e.preventDefault();
        activeElement.nextElementSibling &&
          activeElement.nextElementSibling.focus();
      } else if (isHumanRow && e.key === "ArrowUp") {
        e.preventDefault();
        activeElement.previousElementSibling &&
          activeElement.previousElementSibling.focus();
      }
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, []);

  // TODO: for mobile devices we use window scroll instead of element scroll
  // so maybe use it instead of having "Show more" button
  const [renderCount, onScroll, bumpCount] = useInfiniteListScroll(64);

  const humanListRowMemo = useMemo(() => {
    const humanEls = [];

    for (let i = 0; i < renderCount && i < humans.length; i++) {
      const human = humans[i];

      const humanComments = [];
      for (let c of comments) {
        if (c.humanId === human.id) humanComments.push(c);
      }
      humanComments.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

      humanEls.push(
        <HumanListRow
          key={human.id}
          comments={humanComments}
          human={human}
          isSelected={human.id === selectedId}
          query={query.searchTerm ? query.searchTerm.toLowerCase() : ""}
        />,
      );
    }

    return humanEls;
  }, [selectedId, humans, renderCount, comments, query]);

  useEffect(() => {
    setInputQuery(query.searchTerm ? query.searchTerm : "");
  }, [query]);

  const onChangeHandler = useCallback(
    (event) => {
      const value = event.target.value;
      setInputQuery(value);
      if (value.trim().length >= 2) {
        dispatch(setHumansFilterQuery({ searchTerm: value }));
      } else if (value.length === 0) {
        dispatch(setHumansFilterQuery({ searchTerm: "" }));
      }
    },
    [dispatch],
  );

  return (
    <HumanListColumnWrapper>
      <div className="flex items-center">
        <div className="block lg:hidden">
          <HumanLogo src="/favicon-64.png" alt="humans" />
        </div>
        <InputWrapper>
          <div
            className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            aria-hidden="true"
          >
            <SearchIcon
              className="mr-3 h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <Input
            data-testid="human-search-input"
            value={inputQuery}
            onChange={onChangeHandler}
            onKeyDown={(event) =>
              event.keyCode === 27
                ? dispatch(setHumansFilterQuery({ searchTerm: "" }))
                : null
            }
            type="text"
            name="search"
            id="human-search-input"
            placeholder="Search"
            className="pl-9 my-1 w-full shadow"
          />
          {inputQuery.length > 0 && (
            <ClearIcon
              onClick={() => dispatch(setHumansFilterQuery({ searchTerm: "" }))}
            >
              <XIcon className="h-5 w-5 text-gray-600" />
            </ClearIcon>
          )}
        </InputWrapper>
      </div>

      <AddHumanWrapper>
        <Button
          intent="link"
          onClick={() => {
            history.push({ pathname: "/h/", state: { highlightName: true } });
          }}
        >
          <UserAddIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add human
        </Button>
      </AddHumanWrapper>

      <HumanListWrapper id="human-list" tabIndex="-1" onScroll={onScroll}>
        {loadingHumans && <span>Loading...</span>}
        {!loadingHumans && humans.length === 0 && (
          <div className="text-center mt-2">No results</div>
        )}
        {humanListRowMemo}
        {!loadingHumans && humans.length > 0 && renderCount < humans.length && (
          <>
            <div className="text-center py-2">
              <Button intent="link" tabIndex="-1" onClick={bumpCount}>
                Show more
              </Button>
            </div>
          </>
        )}
      </HumanListWrapper>
    </HumanListColumnWrapper>
  );
};

HumanListColumn.propTypes = {
  selectedId: PropTypes.string,
};

export default HumanListColumn;
